import { useState, useCallback, useEffect, useRef } from "react";
import customerMeasurementService from "../services/customerMeasurementService";
import toast from "react-hot-toast";

export const useCustomerMeasurements = (params = {}) => {
  const [measurements, setMeasurements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(null);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const lastRequestTimeRef = useRef(0);
  const THROTTLE_DELAY = 1000;

  const fetchMeasurements = useCallback(async () => {
    const now = Date.now();
    if (now - lastRequestTimeRef.current < THROTTLE_DELAY) return;
    lastRequestTimeRef.current = now;

    setIsLoading(true);
    try {
      const apiParams = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(categoryFilter ? { garmentCategory: categoryFilter } : {}),
        ...params,
      };
      const response = await customerMeasurementService.getAll(apiParams);
      setMeasurements(response.data.measurements || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch customer measurements"
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, categoryFilter, params]);

  // Debounced search
  useEffect(() => {
    const id = setTimeout(() => {
      setCurrentPage(1);
      fetchMeasurements();
    }, 500);
    return () => clearTimeout(id);
  }, [searchTerm, categoryFilter]);

  useEffect(() => {
    fetchMeasurements();
  }, [currentPage]);

  const createMeasurement = useCallback(
    async (data) => {
      setIsCreating(true);
      try {
        await customerMeasurementService.create(data);
        toast.success("Measurement created successfully!");
        setShowModal(false);
        setEditingMeasurement(null);
        await fetchMeasurements();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to create measurement"
        );
      } finally {
        setIsCreating(false);
      }
    },
    [fetchMeasurements]
  );

  const updateMeasurement = useCallback(
    async (id, data) => {
      setIsUpdating(true);
      try {
        await customerMeasurementService.update(id, data);
        toast.success("Measurement updated successfully!");
        setShowModal(false);
        setEditingMeasurement(null);
        await fetchMeasurements();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to update measurement"
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchMeasurements]
  );

  const deleteMeasurement = useCallback(
    async (id) => {
      setIsDeleting(true);
      try {
        await customerMeasurementService.delete(id);
        toast.success("Measurement deleted successfully!");
        await fetchMeasurements();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to delete measurement"
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchMeasurements]
  );

  const handleEdit = useCallback((measurement) => {
    setEditingMeasurement(measurement);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    (measurement) => {
      if (
        window.confirm(
          "Are you sure you want to delete this measurement?"
        )
      ) {
        deleteMeasurement(measurement.id);
      }
    },
    [deleteMeasurement]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingMeasurement(null);
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      if (editingMeasurement?.id) {
        await updateMeasurement(editingMeasurement.id, data);
      } else {
        await createMeasurement(data);
      }
    },
    [editingMeasurement, createMeasurement, updateMeasurement]
  );

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setCategoryFilter("");
    setCurrentPage(1);
  }, []);

  return {
    measurements,
    isLoading,
    totalPages,
    totalItems,
    currentPage,
    isCreating,
    isUpdating,
    isDeleting,
    showModal,
    editingMeasurement,
    searchTerm,
    categoryFilter,
    setSearchTerm,
    setCategoryFilter,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleCloseModal,
    handlePageChange,
    handleClearFilters,
    fetchMeasurements,
  };
};

export default useCustomerMeasurements;
